import { useState, useRef } from 'react'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import OnlinePaymentService from 'services/OnlinePayment'
import MaskedInputService from 'services/maskedInput'
import CreditCardInput from 'components/Inputs/CreditCardInput'
import onlinePaymentCardValidator from 'validators/onlinePaymentCardValidator'
import { ReactComponent as ButtonLoading } from 'icons/loading.svg'
import { Formik } from 'formik'
import * as FA from 'react-icons/fa'

import {
  SubContainer,
  FormField,
  FormRow,
  Input,
  MaskedInput,
  Button,
  InfoText,
  SubTitle,
  WarningText,
  ErrorContainer,
  MoneyTextMask
} from '../../styles'

/*
  Cartão de crédito do sinal.

  O cartão é digitado aqui, mas **quem o tokeniza é a `cutilei-api`**: o Asaas não tem chave
  pública, então tokenizar no navegador exigiria a chave privada no bundle. O fluxo é em dois
  passos — `card_token` troca os dados do cartão por um token, `card` cobra com esse token —
  e o número do cartão não é guardado em lugar nenhum, nem aqui nem lá.

  Por isso também nenhum `catch` deste arquivo registra o erro cru: `logApiError` já omite o
  corpo da requisição, e é ele que precisa ser usado. Um `console.log (error)` aqui
  imprimiria o cartão no DevTools, que o build de produção não remove.
*/
export default function CardForm ({
  businessId,
  customerId,
  intended = [],
  value,
  customer = {},
  onPaid: handlePaid = () => null,
  onCharged: handleCharged = () => null
}) {
  const [errorMessage, setErrorMessage] = useState (null)
  const idempotencyKey = useRef (OnlinePaymentService.newIdempotencyKey ())

  /* O que a cobrança precisa saber do que ainda não foi agendado. */
  const schedules = intended.map (item => ({
    date: item.date,
    time: item.time,
    serviceId: item.serviceId,
    professionalId: item.professionalId
  }))

  return (
    <Formik
      validationSchema={onlinePaymentCardValidator}
      initialValues={{
        number: '',
        holderName: '',
        expiry: '',
        ccv: '',
        cpf: '',
        postalCode: '',
        addressNumber: ''
      }}
      onSubmit={async (values, { setSubmitting }) => {
        setErrorMessage (null)

        try {
          const { expiryMonth, expiryYear } = OnlinePaymentService.parseCardExpiry (values.expiry)

          const { data: token } = await cutileiApi.post (
            `/businesses/${businessId}/deposit/card_token`, {
              customerId,
              creditCard: {
                holderName: values.holderName.trim (),
                number: values.number.replace (/\D/g, ''),
                expiryMonth,
                expiryYear,
                ccv: values.ccv
              },
              holderInfo: {
                name: values.holderName.trim (),
                email: customer.email ?? '',
                cpfCnpj: values.cpf.replace (/\D/g, ''),
                postalCode: values.postalCode.replace (/\D/g, ''),
                addressNumber: values.addressNumber.trim (),
                phone: customer.phone ?? ''
              }
            },
            { anonymous: true }
          )

          /*
            É esta chamada que segura o horário e cobra: no cartão a resposta já vem
            confirmada, então o agendamento nasce junto e volta na mesma resposta.
          */
          const { data: payment } = await cutileiApi.post (
            `/businesses/${businessId}/deposit/card`,
            { customerId, schedules, creditCardToken: token.creditCardToken },
            { anonymous: true, headers: {'X-Idempotency-Key': idempotencyKey.current} }
          )

          setSubmitting (false)
          handleCharged (payment)
          handlePaid (payment, payment.schedules ?? [])
        } catch (error) {
          logApiError (error)
          setSubmitting (false)
          setErrorMessage (getApiErrorMessage (error))

          /*
            Recusa é uma cobrança que não aconteceu: a próxima tentativa é nova e precisa de
            chave nova, senão a API devolveria a mesma recusa. Falha sem resposta é o
            contrário — pode ter cobrado, e repetir com a mesma chave é o que evita o
            segundo débito.
          */
          if (!OnlinePaymentService.shouldKeepIdempotencyKey (error)) {
            idempotencyKey.current = OnlinePaymentService.newIdempotencyKey ()
          }
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        handleChange,
        handleBlur,
        handleSubmit
      }) => (
        <SubContainer>
          <InfoText style={{color: '#666666', marginBottom: 5}}>
            Pagamento em uma única parcela, processado com segurança.
          </InfoText>
          <FormField style={{margin: 0}}>
            <CreditCardInput
              name='number'
              placeholder='Número do cartão'
              value={values.number}
              onChange={setFieldValue}
              onBlur={handleBlur ('number')}
            />
            {touched.number && errors.number && (
              <WarningText>{errors.number}</WarningText>
            )}
          </FormField>
          <FormField>
            <Input
              placeholder='Nome impresso no cartão'
              value={values.holderName}
              onChange={handleChange ('holderName')}
              onBlur={handleBlur ('holderName')}
            />
            {touched.holderName && errors.holderName && (
              <WarningText>{errors.holderName}</WarningText>
            )}
          </FormField>
          <FormRow>
            <FormField>
              <MaskedInput
                name='expiry'
                placeholder='Validade (MM/AA)'
                options={{date: true, datePattern: ['m', 'y']}}
                value={values.expiry}
                onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                onBlur={handleBlur ('expiry')}
              />
              {touched.expiry && errors.expiry && (
                <WarningText>{errors.expiry}</WarningText>
              )}
            </FormField>
            <FormField>
              <MaskedInput
                name='ccv'
                placeholder='CVV'
                options={{blocks: [4], numericOnly: true}}
                value={values.ccv}
                onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                onBlur={handleBlur ('ccv')}
              />
              {touched.ccv && errors.ccv && (
                <WarningText>{errors.ccv}</WarningText>
              )}
            </FormField>
          </FormRow>
          <FormField>
            <MaskedInput
              name='cpf'
              placeholder='CPF do titular'
              options={{blocks: [3, 3, 3, 2], delimiters: ['.', '.', '-'], numericOnly: true}}
              value={values.cpf}
              onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
              onBlur={handleBlur ('cpf')}
            />
            {touched.cpf && errors.cpf && (
              <WarningText>{errors.cpf}</WarningText>
            )}
          </FormField>
          <FormRow>
            <FormField>
              <MaskedInput
                name='postalCode'
                placeholder='CEP do titular'
                options={{blocks: [5, 3], delimiters: ['-'], numericOnly: true}}
                value={values.postalCode}
                onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                onBlur={handleBlur ('postalCode')}
              />
              {touched.postalCode && errors.postalCode && (
                <WarningText>{errors.postalCode}</WarningText>
              )}
            </FormField>
            <FormField>
              <Input
                placeholder='Número'
                value={values.addressNumber}
                onChange={handleChange ('addressNumber')}
                onBlur={handleBlur ('addressNumber')}
              />
              {touched.addressNumber && errors.addressNumber && (
                <WarningText>{errors.addressNumber}</WarningText>
              )}
            </FormField>
          </FormRow>
          {errorMessage && (
            <ErrorContainer>
              <InfoText style={{color: '#FFFFFF'}}>{errorMessage}</InfoText>
            </ErrorContainer>
          )}
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{marginTop: 15, minWidth: 200}}
          >
            {isSubmitting ? <ButtonLoading/> : (
              <>
                <FA.FaLock size={13}/>
                <SubTitle style={{color: '#FFFFFF'}}>Pagar</SubTitle>
                <MoneyTextMask value={value} style={{color: '#FFFFFF'}}/>
              </>
            )}
          </Button>
        </SubContainer>
      )}
    </Formik>
  )
}
